import { Box, Grid, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Link from '@material-ui/core/Link'
import IconContentAdd from '@material-ui/icons/Add'
import IconCancel from '@material-ui/icons/Cancel'
import axios from 'axios'
import { CREATED, OK } from 'http-status-codes'
import { get } from 'lodash'
import { useEffect, useState } from 'react'
import {
  ArrayField,
  BooleanInput,
  Button as RaButton,
  Datagrid,
  EditView,
  FormDataConsumer,
  FormWithRedirect,
  FunctionField,
  Identifier,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  SingleFieldList,
  TextField,
  TextInput,
  Toolbar,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin'
import { FlexBox } from 'shared/components/Base/Base'

import { detectEnvironment, Environment } from 'config/config'
import { captureExceptionIfProduction, getVGSUrl } from 'helpers/utils'
import { patchRequest, postRequest } from '../../api/common'
import { getTeamMembersELDAccounts, partialUpdateELDAccount } from '../../api/core'
import {
  ELDProviderUrl,
  ELDType,
  ELDTypeChoices,
  ELDTypeName,
  EquipmentType,
  IntegrationStatuses,
} from '../../helpers/constants'
import { withDisableBackdropClick } from '../../helpers/withDisableBackdropClick'
import { ProductSubscription } from '../../models/subscription'
import { BaseActionDetailButton } from '../Common/ActionButtons'
import { EquipmentQuickCreateUpdateButton } from '../Equipment/EquipmentQuickCreateUpdateButton'
import { UserLink } from '../User/UserLink'

export const EldAccountLabel = (props: any) => {
  const { record, source } = props
  if (!record) {
    return null
  }
  const eld_account = get(record, source)
  let text_display = ''
  let color = 'blue'
  if (eld_account) {
    text_display = eld_account.is_active ? 'ACTIVE' : 'INACTIVE'
    color = eld_account.is_active ? 'green' : 'blue'
  } else {
    text_display = 'No ELD Setup'
  }
  return <span style={{ color: color }}>{text_display}</span>
}

function resetCredentials(id: Identifier) {
  const url = `/api/v1/console/connected-integrations/${id}/reset/`
  return axios.post(url).then((response) => {
    if (response.status === OK) {
      return response.data
    }
  })
}

const EditButton = (props: any) => {
  const { record } = props
  const notify = useNotify()
  const refresh = useRefresh()
  const apiUrl = `${getVGSUrl()}/api/v1/console/connected-integrations/`
  const url = `${apiUrl}${record.id}/`
  const method = patchRequest
  const withCredentials = detectEnvironment() === Environment.DEVELOPMENT ? false : true

  const handleClick = (values: any) => {
    const payload = {
      username: values.username,
      password_encrypted: values.password_encrypted,
      api_key_encrypted: values.api_key_encrypted,
      status: IntegrationStatuses.Connected,
    }

    method(url, payload, { withCredentials }).then(() => {
      notify('Credentials updated', { type: 'success' })
      refresh()
    })
  }

  return <SaveButton {...props} onSave={handleClick} />
}

const CredentialsEditToolbar = (props: any) => (
  <Toolbar {...props}>
    <EditButton label="Edit" submitOnEnter={false} variant="text" {...props} />
  </Toolbar>
)

const CredentialsEdit = (props: any) => {
  const { record } = props
  const credentials = {
    id: record.id,
    username: props.suggUsername,
    password_encrypted: props.suggPassword,
    api_key_encrypted: null,
  }
  return (
    // @ts-ignore
    <EditView record={credentials}>
      <SimpleForm toolbar={<CredentialsEditToolbar {...props} />}>
        <TextInput source="username" label="Username" validate={required()} />
        <TextInput source="password_encrypted" label="Password" validate={required()} />
        <TextInput source="api_key_encrypted" label="API KEY" validate={required()} />
      </SimpleForm>
    </EditView>
  )
}

const EditCredentialsDialogButton = (props: any) => {
  const [open, setOpen] = useState(false)
  const dialogTitle = 'Team Admin Credentials'

  return (
    <>
      <Button size="medium" variant="contained" color="primary" onClick={() => setOpen(true)}>
        Confirm
      </Button>
      <Dialog open={open} onClose={withDisableBackdropClick(() => setOpen(false))}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <CredentialsEdit {...props} />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const ShowSuggestedCredentials = (props: any) => {
  const [open, setOpen] = useState(false)
  const dialogTitle = props.children
  return (
    <>
      <Button variant="text" color="primary" onClick={() => setOpen(true)}>
        {props.children}
      </Button>
      <Dialog open={open} onClose={withDisableBackdropClick(() => setOpen(false))}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span>Username: {props.suggUsername}</span>
          </DialogContentText>
          <DialogContentText>
            <span>Password: {props.suggPassword}</span>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setOpen(false)}>
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const ShowCredentialsDialogButton = (props: any) => {
  const { record } = props
  const dummyCredential = '***********'
  const [open, setOpen] = useState(false)
  const [revealedCredentials, setRevealedCredentials] = useState(false)
  const [password, setPassword] = useState(dummyCredential)
  const [apiKey, setApiKey] = useState(dummyCredential)
  const dialogTitle = props.children
  useEffect(() => {
    const url = `${getVGSUrl()}/api/v1/console/connected-integrations/${record.id}/reveal/`
    const withCredentials = detectEnvironment() === Environment.DEVELOPMENT ? false : true
    if (revealedCredentials) {
      setPassword('loading...')
      setApiKey('loading...')
      postRequest(url, null, { withCredentials }).then((data) => {
        setPassword(data.password_encrypted)
        setApiKey(data.api_key_encrypted)
      })
    } else {
      setPassword(dummyCredential)
      setApiKey(dummyCredential)
    }
  }, [revealedCredentials, props, record.id])
  return (
    <>
      <Button variant="text" color="primary" onClick={() => setOpen(true)}>
        {props.children}
      </Button>
      <Dialog open={open} onClose={withDisableBackdropClick(() => setOpen(false))}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span>Username: {record.username}</span>
          </DialogContentText>
          <DialogContentText>
            <span>Password: {password}</span>
          </DialogContentText>
          {record.api_key_encrypted && (
            <DialogContentText>
              <span>API KEY: {apiKey}</span>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setRevealedCredentials(!revealedCredentials)}>
            {revealedCredentials ? 'Hide' : 'Reveal'}
          </Button>
          <Button color="secondary" onClick={() => setOpen(false)}>
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function get_eld_provider(team: any) {
  const integrations = team.connected_integrations
  if (!integrations) return null
  for (var i = 0; i < integrations.length; i++) {
    if (
      integrations[i].name === ELDType.Samsara ||
      integrations[i].name === ELDType.KeepTruckin ||
      Object.values(ELDType).includes(integrations[i].name)
    ) {
      return integrations[i]
    }
  }
  return null
}

const ELDSection = (props: any) => {
  const { record } = props
  const eld_provider = get_eld_provider(record)
  const [suggUsername, setsuggUsername] = useState('loading...')
  const [suggPassword, setsuggPassword] = useState('loading...')
  useEffect(() => {
    if (eld_provider && eld_provider.status === IntegrationStatuses.Pending) {
      const url = `/api/v1/console/connected-integrations/${eld_provider.id}/suggested-credentials/`
      axios.get(url).then((response) => {
        if (response.status === OK) {
          setsuggUsername(response.data.username)
          setsuggPassword(response.data.password)
        }
      })
    }
  }, [eld_provider])

  if (!eld_provider) {
    return (
      <Typography variant="body1" component="span">
        This team has not entered their ELD provider.
      </Typography>
    )
  }
  const status = eld_provider.status
  const isPending = status === IntegrationStatuses.Pending
  const isConnected = status === IntegrationStatuses.Connected
  const isNotStarted = status === IntegrationStatuses.NotStarted
  const provider_name = ELDTypeName[eld_provider.name]
  const provider_url = ELDProviderUrl[eld_provider.name]
  const is_trucker_cloud = provider_name === ELDTypeName.TRUCKERCLOUD

  return (
    <Grid item container xs={8} spacing={4} alignItems="center">
      <Grid item>
        <Typography variant="body1" component="span">
          Provider:{' '}
          <Link href={provider_url} target={'_blank'}>
            {provider_name}{' '}
          </Link>
        </Typography>
      </Grid>
      {is_trucker_cloud && (
        <Grid item>
          <Typography variant="body1" component="span">
            Status: {status}
          </Typography>
        </Grid>
      )}
      {isPending && !is_trucker_cloud && (
        <>
          <Grid item>
            <ShowCredentialsDialogButton record={eld_provider}>
              Fleet Admin Credentials
            </ShowCredentialsDialogButton>
          </Grid>
          <Grid item>
            <ShowSuggestedCredentials
              record={eld_provider}
              suggUsername={suggUsername}
              suggPassword={suggPassword}
            >
              Suggested Credentials
            </ShowSuggestedCredentials>
          </Grid>
          <Grid item>
            <EditCredentialsDialogButton
              record={eld_provider}
              suggUsername={suggUsername}
              suggPassword={suggPassword}
            >
              Edit Credentials
            </EditCredentialsDialogButton>
          </Grid>
        </>
      )}
      {isConnected && !is_trucker_cloud && (
        <Grid item>
          <ShowCredentialsDialogButton record={eld_provider}>Saved Credentials</ShowCredentialsDialogButton>
        </Grid>
      )}
      {(isConnected || isPending) && !is_trucker_cloud && (
        <Grid item>
          <BaseActionDetailButton
            record={eld_provider}
            actionRequest={resetCredentials}
            actionTitle="Reset"
            actionText="This Eld Account will get reset. Are you sure?"
          />
        </Grid>
      )}
      {isNotStarted && (
        <>
          <Grid item>
            <Typography variant="body1" component="span">
              ELD Account has not been set up.
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  )
}

const ELDCreateMethod = {
  automatic: 'automatic',
  manual: 'manual',
}

const ELDUpdateAction = {
  add: 'Add',
  edit: 'Edit',
}

function EldForm(props: any) {
  const { action } = props
  const isEdit = action === ELDUpdateAction.edit
  const isAdd = action === ELDUpdateAction.add
  const ELDCreateMethodChoices = [
    { id: ELDCreateMethod.automatic, name: ELDCreateMethod.automatic },
    { id: ELDCreateMethod.manual, name: ELDCreateMethod.manual },
  ]

  return (
    <Box display="flex" flexDirection="column">
      <>
        {isAdd && (
          <SelectInput
            source="method"
            choices={ELDCreateMethodChoices}
            label="ELD create method"
            validate={required()}
          />
        )}
        <FormDataConsumer>
          {({ formData }) => {
            const isManual = formData.method === ELDCreateMethod.manual
            return (
              <>
                {(isManual || isEdit) && (
                  <TextInput source="provider_id" label="ELD provider id" validate={required()} />
                )}
                {(isManual || isEdit) && (
                  <TextInput source="provider_username" label="ELD provider username" />
                )}
                {(formData.method || isEdit) && (
                  <SelectInput
                    source="provider_type"
                    choices={ELDTypeChoices}
                    label="ELD provider type"
                    validate={required()}
                  />
                )}
                {isEdit && <BooleanInput source="is_active" label="is active" />}
              </>
            )
          }}
        </FormDataConsumer>
      </>
    </Box>
  )
}

function addEldAutomatic(payload: any) {
  let url = `/api/v1/console/eld-accounts/automatic-create/`
  return axios.post(url, payload).then((response) => {
    if (response.status === CREATED) {
      return response.data
    }
  })
}

function EldUpdateButton(props: any) {
  const { record, source } = props
  const [showDialog, setShowDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const dataProvider = useDataProvider()
  const resourceName = 'eld-accounts'
  const eld_account = get(record, source)
  const actionString = eld_account ? ELDUpdateAction.edit : ELDUpdateAction.add

  const handleClick = () => setShowDialog(true)

  const handleClose = () => setShowDialog(false)

  const eldAccountLabel = 'ELD Account'

  const handleSubmit = async (values: any) => {
    setLoading(true)
    let requestDataProvider
    if (eld_account) {
      const payload: Record<string, any> = {}
      Object.keys(values).forEach((key) => {
        if (values[key] !== record[key]) {
          payload[key] = values[key]
        }
      })
      requestDataProvider = partialUpdateELDAccount(eld_account.id, payload)
    } else {
      let eldCreateMethod = values.method
      delete values.method
      const payload = values
      payload['driver'] = record.id
      if (eldCreateMethod === ELDCreateMethod.manual) {
        requestDataProvider = dataProvider.create(resourceName, {
          data: payload,
        })
      } else if (eldCreateMethod === ELDCreateMethod.automatic) {
        requestDataProvider = addEldAutomatic(payload)
      }
    }

    if (!requestDataProvider) {
      return
    }

    requestDataProvider
      .then(() => {
        notify(`${eldAccountLabel} updated!`, { type: 'success' })
        refresh()
        setShowDialog(false)
      })
      .catch((error) => {
        notify(`Oops! Something went wrong: ${error.message}`, { type: 'error' })
        captureExceptionIfProduction(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const initialValues = eld_account

  return (
    <>
      <RaButton onClick={handleClick} label={actionString}>
        <IconContentAdd />
      </RaButton>
      <Dialog
        fullWidth
        open={showDialog}
        onClose={withDisableBackdropClick(handleClose)}
        aria-label={`${actionString} ${eldAccountLabel}`}
      >
        <DialogTitle>
          {actionString} {eldAccountLabel}
        </DialogTitle>
        <FormWithRedirect
          initialValues={initialValues}
          resource={resourceName}
          save={handleSubmit}
          render={({ handleSubmitWithRedirect, saving }) => (
            <>
              <DialogContent>
                <EldForm action={actionString} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                  <IconCancel />
                </Button>
                <SaveButton
                  handleSubmitWithRedirect={handleSubmitWithRedirect}
                  saving={saving}
                  disabled={loading}
                />
              </DialogActions>
            </>
          )}
        />
      </Dialog>
    </>
  )
}

const ELDPremiumSection = (props: any) => {
  const { record } = props
  const [teamMembersEldAccounts, setTeamMembersEldAccounts] = useState<any>(null)
  const [isLoading, setLoading] = useState(false)
  const notify = useNotify()

  useEffect(() => {
    setLoading(true)
    getTeamMembersELDAccounts(record.id)
      .then((response) => {
        setTeamMembersEldAccounts(response)
        setLoading(false)
      })
      .catch((error) => {
        notify('Oops, could not load eld accounts', { type: 'error' })
        captureExceptionIfProduction(error)
        setLoading(false)
      })
  }, [record.id, notify])

  return isLoading ? (
    <CircularProgress color="inherit" size={20} />
  ) : (
    <>
      <Typography variant="h5" style={{ marginLeft: '5px', marginTop: '30px', marginBottom: '10px' }}>
        Drivers
      </Typography>
      <ArrayField record={teamMembersEldAccounts} source="members">
        <Datagrid>
          <FunctionField label="Name" render={(record: any) => <UserLink user={record} />} />
          <TextField source="eld_accounts[0].provider_id" label="ELD provider id" />
          <TextField source="eld_accounts[0].provider_username" label="ELD provider username" />
          <TextField source="eld_accounts[0].provider_type" label="ELD provider type" />
          <EldAccountLabel label="Eld status" source="eld_accounts[0]" />
          <EldUpdateButton source="eld_accounts[0]" />
        </Datagrid>
      </ArrayField>
      <Typography variant="h5" style={{ marginLeft: '5px', marginTop: '40px', marginBottom: '10px' }}>
        Trucks
      </Typography>
      <ArrayField record={record} source="trucks">
        <Datagrid>
          <ArrayField source="drivers" label="Assigned drivers">
            <SingleFieldList linkType={false}>
              <FunctionField
                source="full_name"
                render={(record: any) => (
                  <FlexBox mr={1}>
                    <UserLink user={record} />
                  </FlexBox>
                )}
              />
            </SingleFieldList>
          </ArrayField>

          <TextField source="eld_type" label="ELD provider type" />
          <TextField source="eld_serial_number" label="Eld serial number" />
          <TextField source="license_plate_number" label="Truck Plate" />
          <EquipmentQuickCreateUpdateButton
            record={record}
            equipmentType={EquipmentType.Truck}
            isCreate={false}
          />
        </Datagrid>
      </ArrayField>
    </>
  )
}

export function ELDTab(props: any) {
  const { record } = props
  const isPremium = record.product_subscription.product_name === ProductSubscription.PREMIUM
  if (isPremium) {
    return ELDPremiumSection(props)
  }
  return ELDSection(props)
}
